import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_BOOTSTRAP_LISTENER, ErrorHandler, Injector, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutComponent } from './layout.component';
import { DebugComponent } from './debug/debug.component';
import { HealthComponent } from './health/health.component';

import { TranslateModule } from '@ngx-translate/core';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuard,
  MsalInterceptor,
  MsalService,
  MsalBroadcastService,
} from '@azure/msal-angular';

import { APP_BASE_HREF } from '@angular/common';

import { env } from '../environments/env';

// App modules
import { CoreModule } from './core/core.module';
import { HeaderModule } from './header';
import { AdminModule } from '@admin/admin.module';
import { WorkOrderModule } from '@app/work-order/work-order.module';
import { DashboardModule } from '@app/dashboard/dashboard.module';
import { ErrorDialogManagerModule } from '@app/shared/errors/error-dialog-manager/error-dialog-manager.module';
import { ErrorPageModule } from '@app/shared/errors/error-page/error-page.module';
import { OfflineToolbarModule } from '@app/shared/offline-toolbar/offline-toolbar.module';
import { ProfileModule } from '@app/profile/profile.module';
import { WorkLineModule } from '@app/shared/work-line/work-line.module';
import { ConfirmModule } from '@app/shared/confirm/confirm.module';

import { ApiModule, Configuration } from '@app/generated';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  initializeMsal,
} from '@app/auth-config';
import { CallbackComponent } from './auth/callback.component';
import { VersionCheckService } from '@app/core/services/version-check.service';
import { isE2eBypassEnabled } from './auth/e2e-bypass';
import { E2eAuthInterceptor } from './auth/e2e-auth.interceptor';
import { SupportModule } from '@app/support/support.module';
import { AppInsightsService } from '@app/core/services/app-insights.service';
import { AppInsightsErrorHandler } from '@app/shared/handlers/app-insights-error-handler';

@NgModule({
  declarations: [AppComponent, LayoutComponent, DebugComponent, HealthComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    CallbackComponent,
    ApiModule.forRoot(
      () =>
        new Configuration({
          basePath: env.get().api.url,
        }),
    ),

    AppRoutingModule,
    RouterModule,
    NgxSliderModule,

    // App modules
    CoreModule,
    ProfileModule,
    HeaderModule,
    AdminModule,
    WorkOrderModule,
    DashboardModule,
    ErrorPageModule,
    ErrorDialogManagerModule,
    OfflineToolbarModule,
    ConfirmModule,
    WorkLineModule,
    MatProgressSpinner,
    SupportModule,
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },

    provideAppInitializer(() => inject(AppInsightsService).init()),
    { provide: ErrorHandler, useClass: AppInsightsErrorHandler },

    // APP_BOOTSTRAP_LISTENER ensures MSAL redirect handling completes BEFORE routing.
    // When the e2e auth bypass is active it is a no-op so no redirect is triggered.
    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: (msalService: MsalService, versionCheckService: VersionCheckService) => {
        return () => {
          if (isE2eBypassEnabled()) {
            return;
          }
          initializeMsal(msalService, versionCheckService)();
        };
      },
      deps: [MsalService, VersionCheckService],
      multi: true,
    },

    // Statically-declared interceptor provider (AOT-safe): the NgModule metadata
    // is fully analyzable and the runtime decision — real MSAL vs. the e2e header
    // bypass — is deferred into the factory instead of branching in the array.
    MsalInterceptor,
    E2eAuthInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (injector: Injector) =>
        isE2eBypassEnabled() ? injector.get(E2eAuthInterceptor) : injector.get(MsalInterceptor),
      deps: [Injector],
      multi: true,
    },

    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },

    MsalService,
    MsalGuard,
    MsalBroadcastService,

    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    console.log('[BOOT] AppModule constructed');
  }
}
